import { Box } from "@mui/material";
import { useRef, useEffect, useState } from "react";


function animateFoobarStyle(analyser, canvas, canvasCtx, dataArray, bufferLength, peakPositions) {
  analyser.getByteFrequencyData(dataArray);
  
  // Clear the canvas with a semi-transparent black to create trail effect
  canvasCtx.fillStyle = "rgba(0, 0, 0, 0.3)";
  canvasCtx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Constants for visualization
  const barSpacing = 2;
  const barWidth = Math.max(2, Math.floor((canvas.width / bufferLength) * 2.5) - barSpacing);
  const baseHeight = canvas.height;
  
  // Reduce the multiplier to cut heights earlier
  const multiplier = 1.8;
  
  // Add a height cap as a percentage of canvas height
  const maxBarHeight = baseHeight * 0.8;
  
  // Start X position
  let x = 0;
  
  // Pre-compute positions for smoother animation
  const positions = [];
  
  // Calculate bar heights and store positions
  for (let i = 0; i < bufferLength; i++) {
    if (i % 2 === 0 || i < 5) {
      const value = dataArray[i] / 255.0;
      const easedValue = Math.pow(value, 1.7);
      const barHeight = Math.min(maxBarHeight, easedValue * baseHeight * multiplier);
      
      positions.push({
        x: x,
        height: barHeight,
        value: easedValue,
        index: i
      });
      
      x += barWidth + barSpacing;
    }
  }
  
  // Draw gradient background bars
  const gradient = canvasCtx.createLinearGradient(0, 0, 0, baseHeight);
  gradient.addColorStop(0, "#00FF00");
  gradient.addColorStop(0.5, "#AAFF00");
  gradient.addColorStop(1, "#005500");
  
  canvasCtx.fillStyle = gradient;
  
  // Draw bars with reflection effect
  positions.forEach((pos, idx) => {
    // Main bar
    canvasCtx.fillRect(
      pos.x, 
      baseHeight - pos.height, 
      barWidth, 
      pos.height
    );
    
    // Update peak positions
    // If current bar height is higher than the peak height, reset peak to new height
    if (pos.height > peakPositions[idx] || peakPositions[idx] === undefined) {
      peakPositions[idx] = pos.height;
    } else {
      // Otherwise, make the peak fall slowly
      // The smaller the fallSpeed value, the slower it falls
      const fallSpeed = 0.5; // Slowed down from the typical 1.0-1.5
      peakPositions[idx] -= fallSpeed;
    }
    
    // Reflection with gradient overlay
    const reflectionGradient = canvasCtx.createLinearGradient(0, baseHeight, 0, baseHeight + 30);
    reflectionGradient.addColorStop(0, "rgba(0, 255, 0, 0.3)");
    reflectionGradient.addColorStop(1, "rgba(0, 255, 0, 0)");
    
    canvasCtx.fillStyle = reflectionGradient;
    canvasCtx.fillRect(
      pos.x, 
      baseHeight, 
      barWidth, 
      Math.min(15, 20 * pos.value)
    );
    
    // Reset fill style for next main bar
    canvasCtx.fillStyle = gradient;
  });
  
  // Draw peak indicators that stay at top and fall slowly
  canvasCtx.fillStyle = "#FFFFFF";
  positions.forEach((pos, idx) => {
    // Only draw peaks if they're visible
    if (peakPositions[idx] > 0) {
      canvasCtx.fillRect(
        pos.x, 
        baseHeight - peakPositions[idx] - 2, // Position based on peak height
        barWidth, 
        2
      );
    }
  });
  
  return positions.length; // Return the actual number of positions for peak tracking
}

const Visualizer = ({ data }) => {
  const canvasRef = useRef(null);
  const peakPositionsRef = useRef([]);
  const animationRef = useRef(null);
  const { dataArray, analyzer, bufferLength } = data || {dataArray: 0, analyzer: 0, bufferLength: 0};
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  
  // Handle resize to make the canvas responsive
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        const containerWidth = canvas.parentElement.clientWidth;
        setCanvasSize({
          width: containerWidth * 0.98,
          height: 120
        });
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const draw = (dataArray, analyzer, bufferLength) => {
    const canvas = canvasRef.current;
    if (!canvas || !analyzer) return;
    
    const canvasCtx = canvas.getContext("2d");
    
    // Clean up previous animation frame
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      
      // Set canvas dimensions based on actual size for sharp rendering
      canvas.width = canvasSize.width;
      canvas.height = canvasSize.height;
      
      // Apply the Foobar-style visualization and get actual positions count
      const positionsCount = animateFoobarStyle(
        analyzer, 
        canvas, 
        canvasCtx, 
        dataArray, 
        bufferLength,
        peakPositionsRef.current
      );
      
      // Make sure peakPositions array is the correct length
      if (peakPositionsRef.current.length !== positionsCount) {
        peakPositionsRef.current = new Array(positionsCount).fill(0);
      }
    };
    
    animate();
  };
  
  useEffect(() => {
    if (data !== "undefined") {
      draw(dataArray, analyzer, bufferLength);
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [dataArray, analyzer, bufferLength, canvasSize]);
  
  return (
    <Box sx={{ 
      width: "100%", 
      display: "flex", 
      justifyContent: "center",
      borderRadius: "8px",
      overflow: "hidden",
      boxShadow: "0 4px 8px rgba(0,0,0,0.3)"
    }}>
      <canvas 
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        style={{ 
          backgroundColor: 'black', 
          width: '100%',
          height: `${canvasSize.height}px`,
          borderRadius: "8px",
          display: "block"
        }}
      />
    </Box>
  );
};

export default Visualizer;